import { useState } from 'react';
import { useCatalog, useCreateCatalogItem, useUpdateCatalogItem, useDeleteCatalogItem, CatalogItem } from '../hooks/useCatalog';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Modal } from '@/shared/components/ui/Modal';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { PlusCircle, Pencil, Trash2, Package, Wrench } from 'lucide-react';
import { formatINR } from '@/shared/utils/currency';

const UNIT_OPTIONS = [
  { value: 'nos', label: 'Nos' }, { value: 'hrs', label: 'Hours' }, { value: 'kg', label: 'Kg' },
  { value: 'ltr', label: 'Litre' }, { value: 'mtr', label: 'Metre' }, { value: 'sqft', label: 'Sq.Ft' },
  { value: 'box', label: 'Box' }, { value: 'set', label: 'Set' }, { value: 'month', label: 'Month' }, { value: 'project', label: 'Project' },
];

const EMPTY_FORM = { name: '', type: 'service' as 'product' | 'service', sku: '', hsnSac: '', description: '', unit: 'nos', defaultRate: 0, defaultDiscount: 0, cgstRate: 0, sgstRate: 0, igstRate: 0 };

export function ProductCatalogTab() {
  const [showAll, setShowAll] = useState(false);
  const { data: items, isLoading } = useCatalog(showAll);
  const createMutation = useCreateCatalogItem();
  const updateMutation = useUpdateCatalogItem();
  const deleteMutation = useDeleteCatalogItem();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const openCreate = () => { setForm(EMPTY_FORM); setEditingId(null); setIsFormOpen(true); };
  const openEdit = (item: CatalogItem) => {
    setForm({ name: item.name, type: item.type, sku: item.sku, hsnSac: item.hsnSac, description: item.description, unit: item.unit, defaultRate: item.defaultRate, defaultDiscount: item.defaultDiscount, cgstRate: item.cgstRate, sgstRate: item.sgstRate, igstRate: item.igstRate });
    setEditingId(item._id);
    setIsFormOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) return;
    if (editingId) {
      await updateMutation.mutateAsync({ id: editingId, ...form });
    } else {
      await createMutation.mutateAsync(form);
    }
    setIsFormOpen(false);
  };

  const handleDelete = async (id: string) => {
    if (confirm('Deactivate this item? It won\'t appear in invoice selection anymore.')) {
      await deleteMutation.mutateAsync(id);
    }
  };

  const setField = (key: string, value: any) => setForm(prev => ({ ...prev, [key]: value }));

  if (isLoading) return <Skeleton className="h-[400px]" />;

  return (
    <Card className="shadow-xs">
      <CardHeader
        title="Product & Service Catalog"
        action={
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
              <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} className="rounded" />
              Show inactive
            </label>
            <Button size="sm" onClick={openCreate}><PlusCircle className="h-4 w-4 mr-1" /> Add Item</Button>
          </div>
        }
      />
      <CardBody className="p-0">
        <div className="overflow-auto max-h-[500px]">
          <table className="w-full text-sm text-left">
            <thead className="text-xs uppercase bg-muted/50 text-muted-foreground sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">HSN/SAC</th>
                <th className="px-4 py-3">Unit</th>
                <th className="px-4 py-3 text-right">Rate</th>
                <th className="px-4 py-3 text-center">GST</th>
                <th className="px-4 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {(!items || items.length === 0) && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">No items in catalog. Add your first product or service.</td></tr>
              )}
              {items?.map(item => (
                <tr key={item._id} className={`hover:bg-muted/20 ${!item.isActive ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {item.type === 'product' ? <Package className="h-4 w-4 text-blue-500" /> : <Wrench className="h-4 w-4 text-purple-500" />}
                      <div>
                        <div className="font-medium">{item.name}</div>
                        {item.sku && <div className="text-xs text-muted-foreground">SKU: {item.sku}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 capitalize">{item.type}</td>
                  <td className="px-4 py-3 font-mono text-xs">{item.hsnSac || '-'}</td>
                  <td className="px-4 py-3">{item.unit}</td>
                  <td className="px-4 py-3 text-right font-medium">{formatINR(item.defaultRate)}</td>
                  <td className="px-4 py-3 text-center text-xs">
                    {item.igstRate > 0 ? `IGST ${item.igstRate}%` : (item.cgstRate + item.sgstRate > 0 ? `${item.cgstRate}% + ${item.sgstRate}%` : '-')}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(item)}><Pencil className="h-3.5 w-3.5" /></Button>
                      {item.isActive && <Button variant="ghost" size="sm" onClick={() => handleDelete(item._id)} className="text-destructive"><Trash2 className="h-3.5 w-3.5" /></Button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardBody>

      <Modal open={isFormOpen} onClose={() => setIsFormOpen(false)} title={editingId ? 'Edit Catalog Item' : 'Add Catalog Item'} size="md" footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setIsFormOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={createMutation.isPending || updateMutation.isPending}>
            {(createMutation.isPending || updateMutation.isPending) ? 'Saving...' : (editingId ? 'Update' : 'Create')}
          </Button>
        </div>
      }>
        <div className="grid grid-cols-2 gap-4 py-4">
          <div className="col-span-2"><label className="text-sm font-medium">Name *</label><Input name="name" value={form.name} onChange={e => setField('name', e.target.value)} placeholder="e.g. Website Development" /></div>
          <div><label className="text-sm font-medium">Type</label><Select name="type" value={form.type} onChange={e => setField('type', e.target.value)} options={[{ value: 'service', label: 'Service' }, { value: 'product', label: 'Product' }]} /></div>
          <div><label className="text-sm font-medium">SKU</label><Input name="sku" value={form.sku} onChange={e => setField('sku', e.target.value)} placeholder="Optional" /></div>
          <div><label className="text-sm font-medium">HSN/SAC Code</label><Input name="hsnSac" value={form.hsnSac} onChange={e => setField('hsnSac', e.target.value)} placeholder="e.g. 998314" /></div>
          <div><label className="text-sm font-medium">Unit</label><Select name="unit" value={form.unit} onChange={e => setField('unit', e.target.value)} options={UNIT_OPTIONS} /></div>
          <div><label className="text-sm font-medium">Default Rate (₹)</label><Input name="defaultRate" type="number" value={form.defaultRate} onChange={e => setField('defaultRate', Number(e.target.value))} /></div>
          <div><label className="text-sm font-medium">Default Discount (₹)</label><Input name="defaultDiscount" type="number" value={form.defaultDiscount} onChange={e => setField('defaultDiscount', Number(e.target.value))} /></div>
          <div className="col-span-2"><label className="text-sm font-medium">Description</label><Input name="description" value={form.description} onChange={e => setField('description', e.target.value)} placeholder="Optional description" /></div>
          <div className="col-span-2 border-t pt-4"><p className="text-sm font-medium mb-2">GST Rates (%)</p></div>
          <div><label className="text-xs text-muted-foreground">CGST %</label><Input name="cgstRate" type="number" value={form.cgstRate} onChange={e => setField('cgstRate', Number(e.target.value))} /></div>
          <div><label className="text-xs text-muted-foreground">SGST %</label><Input name="sgstRate" type="number" value={form.sgstRate} onChange={e => setField('sgstRate', Number(e.target.value))} /></div>
          <div><label className="text-xs text-muted-foreground">IGST % (for inter-state)</label><Input name="igstRate" type="number" value={form.igstRate} onChange={e => setField('igstRate', Number(e.target.value))} /></div>
        </div>
      </Modal>
    </Card>
  );
}
